export const SITE_NAME = "Nohaya";
export const SITE_URL = "https://www.nohaya.com";
// Resume examples moved to their own site on 2026-10-24 (fresh domain, no
// redirects); nohaya.com/resumes now answers 410 Gone and links here instead.
export const RESUME_SITE_URL = "https://nohayaresumes.com";
// Same-origin in production (CloudFront routes /api/* to the Lambda API).
// Falls back to the CloudFront domain directly for local builds.
export const API_BASE_URL =
  process.env.NEXT_PUBLIC_API_BASE_URL ?? "https://d319bntb2m01dq.cloudfront.net";
// The site-wide fallback (pages without their own description) -- the same
// positioning as the home page and llms.txt, so search engines and AI
// assistants get one consistent description of what Nohaya is.
export const SITE_DESCRIPTION =
  "Nohaya is a product studio for website design, mobile app development and AI products. It also publishes free resume examples for 7,900+ jobs, a resume builder and local travel guides.";
export const SITE_TAGLINE = "Website design, app development & AI products";
export const SUPPORT_EMAIL = "nohayasite@gmail.com";

// Not secrets (both ship to the client either way) -- hardcoded like
// SITE_URL above rather than env vars, so the PHP-served pages
// (php/site-shared/includes/chrome_head.php) can use the same literal
// values without needing their own env-loading story.
export const GA_MEASUREMENT_ID = "G-EPXPPG0VG3";
export const CLARITY_PROJECT_ID = "xehvv4qu01";

export type NavLink = {
  label: string;
  href: string;
};

// Header nav (Header.tsx) -- centre links on desktop, and the top of the
// menu panel on every screen size. Work/Services are sections of the home
// page.
export const HEADER_LINKS: NavLink[] = [
  { label: "Work", href: "/#work" },
  { label: "Services", href: "/#services" },
  { label: "Places", href: "/places" },
  { label: "Prompts", href: "/prompts" },
  { label: "About", href: "/about" },
];

// "Resources" hover dropdown in the header (and the menu panel). Real links,
// always in the server-rendered HTML. The resume examples and their
// categories live on nohayaresumes.com (same job_domains.domain_slug values).
export const RESOURCE_LINKS = {
  main: {
    label: "Resume Examples",
    href: `${RESUME_SITE_URL}/resume-examples`,
    description: "7,900+ job titles with real examples and 10 free PDF templates, on Nohaya Resumes",
  },
  builder: {
    label: "Resume Builder",
    href: "/resume-builder",
    description: "10 templates, live pages, an exact PDF — free to build",
  },
  categories: [
    { label: "Software Development", href: `${RESUME_SITE_URL}/categories/software-development` },
    { label: "Nursing", href: `${RESUME_SITE_URL}/categories/nursing` },
    { label: "Accounting", href: `${RESUME_SITE_URL}/categories/accounting` },
    { label: "Data & Analytics", href: `${RESUME_SITE_URL}/categories/data-analytics` },
    { label: "Artificial Intelligence", href: `${RESUME_SITE_URL}/categories/artificial-intelligence` },
    { label: "School Education", href: `${RESUME_SITE_URL}/categories/school-education` },
  ] as NavLink[],
};

export type ProductLink = {
  name: string;
  tagline: string;
  href: string;
  icon: string;
  external?: boolean;
};

// "Our products" in the header menu panel.
export const PRODUCT_LINKS: ProductLink[] = [
  {
    name: "Vociva",
    tagline: "AI voice",
    href: "https://play.google.com/store/apps/details?id=com.vociva.app",
    icon: "/home/icon-vociva.webp",
    external: true,
  },
  { name: "Locavia", tagline: "Travel discovery", href: "/locavia", icon: "/home/icon-locavia.webp" },
  { name: "Maila", tagline: "Email cleaner", href: "/maila", icon: "/home/icon-maila.webp" },
  { name: "Stockio", tagline: "Voice billing", href: "/stockio", icon: "/home/icon-stockio.webp" },
  {
    name: "PromptAI",
    tagline: "AI image prompts",
    href: "/prompts",
    icon: "/home/icon-promptai.webp",
  },
];

// Rendered on every page, so these are the site-wide crawl paths: the
// product pages are otherwise only reachable from the home page (the header's
// product list renders only while its menu is open).
export const FOOTER_LINKS: { heading: string; links: NavLink[] }[] = [
  {
    heading: "Products",
    links: [
      { label: "Resume Examples", href: `${RESUME_SITE_URL}/resume-examples` },
      { label: "Resume Builder", href: "/resume-builder" },
      { label: "Places", href: "/places" },
      { label: "Locavia", href: "/locavia" },
      { label: "Maila", href: "/maila" },
      { label: "Stockio", href: "/stockio" },
      { label: "Pricing", href: "/pricing" },
    ],
  },
  {
    heading: "Company",
    links: [
      { label: "About Us", href: "/about" },
      { label: "Careers", href: "/careers" },
      { label: "Contact", href: "/contact" },
    ],
  },
  {
    heading: "Resources",
    links: [
      { label: "Documentation", href: "/docs" },
      { label: "Help Center", href: "/help" },
      { label: "Community", href: "/community" },
      { label: "Status", href: "/status" },
    ],
  },
  {
    heading: "Legal",
    links: [
      { label: "Privacy Policy", href: "/privacy" },
      { label: "Terms of Service", href: "/terms" },
      { label: "Refund Policy", href: "/refund" },
      { label: "Cookies", href: "/cookies" },
    ],
  },
];
